import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaTrophy,
  FaUserTie,
  FaDumbbell,
  FaClock,
  FaUsers,
  FaShieldAlt,
  FaMapMarkerAlt,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaQuoteLeft
} from 'react-icons/fa';
import './About.css';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: <FaDumbbell />,
      title: 'Professional & Experienced Coaching',
      description: 'Our club is led by qualified and experienced badminton coaches who focus on building strong fundamentals while refining advanced skills. Every training session is designed with clear objectives, ensuring consistent progress in technique, footwork, fitness, and match strategy. Personalized attention helps players improve at their own pace.',
    },
    {
      icon: <FaUserTie />,
      title: 'Structured Training Programs',
      description: 'We offer well-planned training programs for beginners, intermediate players, and advanced athletes. Our step-by-step coaching methodology ensures proper skill development, gradual progression, and long-term performance enhancement rather than short-term results.',
    },
    {
      icon: <FaUsers />,
      title: 'Modern Infrastructure & Safe Environment',
      description: 'Net Nest provides international-standard indoor courts with quality flooring and proper lighting, creating a safe and professional training atmosphere. Our facilities support injury prevention and allow players to practice comfortably under match-like conditions.',
    },
    {
      icon: <FaTrophy />,
      title: 'Focus on Fitness & Overall Development',
      description: 'Badminton requires agility, stamina, and strength. We integrate fitness training, flexibility exercises, and conditioning drills into our coaching programs to ensure players remain fit, fast, and injury-free. Mental strength, discipline, and confidence-building are equally emphasized.',
    },
    {
      icon: <FaClock />,
      title: 'Personalized Attention & Performance Monitoring',
      description: 'We believe every player is unique. Our coaches regularly assess individual performance, identify strengths and areas for improvement, and provide constructive feedback. This personalized approach helps players achieve steady and measurable growth.',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Competitive Exposure & Match Practice',
      description: 'We encourage players to participate in practice matches, club tournaments, and external competitions. Regular match exposure helps develop confidence, strategic thinking, and real-game experience essential for competitive success.',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Strong Values & Sportsmanship',
      description: 'At Net Nest Badminton Sports Club, we focus on building character alongside skills. Discipline, respect, teamwork, and fair play are core values we instill in every player, helping them grow both as athletes and individuals.',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Supportive Badminton Community',
      description: 'Our club offers a friendly and motivating atmosphere where players support and inspire each other. Parents and players are welcomed as part of a close-knit community dedicated to continuous learning and improvement.',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Commitment to Excellence',
      description: 'With a passion for badminton and a vision for long-term development, Net Nest Badminton Sports Club remains committed to maintaining high training standards and evolving with modern coaching techniques.',
    },
  ];

  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '5000+', label: 'Happy Members' },
    { number: '12', label: 'Pro Courts' },
    { number: '50+', label: 'Tournaments' }
  ];

  const testimonials = [
    
  ];

  // Testimonials Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-slide effect
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(nextSlide, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  // Pause auto-slide on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  return (
    <div className="why-choose-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header-overlay"></div>
        <div className="container">
          <div className="page-header-content">
            <span className="page-badge">Why Net Nest</span>
            <h1>Why Choose Us</h1>
            <p>Discover what makes Net Nest the premier badminton destination</p>
            <nav className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/about">About</Link>
              <span>/</span>
              <span>Why Choose Us</span>
            </nav>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="why-stats">
        <div className="container">
          <div className="stats-grid-alt">
            {stats.map((stat, index) => (
              <div className="stat-item-alt" key={index}>
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Reasons Section */}
      <section className="reasons-section section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Our Advantages</span>
            <h2>What Sets Us Apart</h2>
            <p>Experience the Net Nest difference through our commitment to excellence</p>
          </div>
          <div className="reasons-grid">
            {reasons.map((reason, index) => (
              <div className="reason-card" key={index}>
                <div className="reason-icon">{reason.icon}</div>
                <h3>{reason.title}</h3>
                <p>{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="location-section section">
        <div className="container">
          <div className="location-grid">
            <div className="location-content">
              <span className="section-badge">Convenient Location</span>
              <h2>Easy to Reach, Hard to Leave</h2>
              <p>
                Strategically located in the heart of the Sports District, Net Nest is
                easily accessible by public transport and offers ample parking for members
                who drive.
              </p>
              <ul className="location-features">
                <li><FaMapMarkerAlt /> Central location in Sports District</li>
                <li><FaCheckCircle /> Free parking for 200+ vehicles</li>
                <li><FaCheckCircle /> 5 minutes from Metro Station</li>
                <li><FaCheckCircle /> Multiple bus routes nearby</li>
                <li><FaCheckCircle /> Wheelchair accessible</li>
              </ul>
              <Link to="/contact" className="btn btn-primary">Get Directions</Link>
            </div>
            <div className="location-image">
              <img src="https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=600" alt="Our Location" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience the Difference?</h2>
            <p>Join Net Nest today and elevate your badminton journey</p>
            <div className="cta-buttons">
              <Link to="/membership" className="btn btn-white">View Membership Plans</Link>
              <Link to="/contact" className="btn btn-outline-white">Book a Tour</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
